import { createContext, useContext } from 'react';

import type { Permission, ProjectAccess } from '../api/types';
import { useMe } from '../auth/context';

export interface ProjectContextValue {
  project: ProjectAccess;
  // Projekte, in die der Benutzer wechseln darf (project.switch).
  switchable: ProjectAccess[];
  can: (permission: Permission) => boolean;
  switchProject: (projectKey: string) => void;
}

export const ProjectContext = createContext<ProjectContextValue | null>(null);

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within a project route');
  return ctx;
}

// Globale Vertriebsrollen ändern die Arbeitssicht nicht: Ein Makler mit eigener Vertriebsstruktur bleibt im
// Außendienst-Modus und sieht zusätzlich „Vertrieb“.
const salesRoleKeys = ['SALES_REP', 'SALES_LEADER'];

// useFieldOnly: reiner Außendienst (Makler) im aktiven Projekt – ohne globale Rolle (außer Vertrieb) und nur mit FIELD_SALES.
export function useFieldOnly(): boolean {
  const me = useMe();
  const { project } = useProject();
  return (
    me.global_roles.every((r) => salesRoleKeys.includes(r.key)) && project.roles.length > 0 && project.roles.every((r) => r.key === 'FIELD_SALES')
  );
}

// Fokus statt Rechte (Dominik 15.10.2026): reiner Support bzw. reine Terminplanung ohne Leitungsrolle – die Rechte bleiben,
// die Oberfläche zeigt nur das Wesentliche für ihr Ziel.
function useOnlyRoles(required: string, allowed: string[]): boolean {
  const me = useMe();
  const { project } = useProject();
  return (
    me.global_roles.every((r) => salesRoleKeys.includes(r.key)) &&
    project.roles.some((r) => r.key === required) &&
    project.roles.every((r) => allowed.includes(r.key))
  );
}

export function useSupportOnly(): boolean {
  return useOnlyRoles('SUPPORT', ['SUPPORT']);
}

export function useTelesalesOnly(): boolean {
  return useOnlyRoles('TELESALES', ['TELESALES', 'CALL_OVERRIDE']);
}

// useIsFieldSales: Der Benutzer ist im Projekt (auch) Außendienst – er hat eigene Termine.
export function useIsFieldSales(): boolean {
  return useProject().project.roles.some((r) => r.key === 'FIELD_SALES');
}
